import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { 
  Card, 
  Typography, 
  Divider, 
  Space, 
  Button, 
  Collapse,
  Table,
  Tag,
  Alert,
  Statistic,
  Row,
  Col,
  Tooltip,
  Progress
} from 'antd'
import { 
  DollarOutlined,
  CalendarOutlined,
  SafetyOutlined,
  DownloadOutlined,
  ShareAltOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseOutlined
} from '@ant-design/icons'
import { useDesignStore, type PoolModel, type ADUModel, type PoolEquipmentModel, type ADUEquipmentModel } from '../../store/designStore'
import { pricingEngine, formatCurrency, type ProjectQuote, type PricingBreakdown } from '../../services/pricingEngine'
import { poolPricingEngine } from '../../utils/poolPricingEngine'

const { Title, Text, Paragraph } = Typography
const { Panel } = Collapse

interface PricingPanelProps {
  onClose: () => void
}

const PricingPanel: React.FC<PricingPanelProps> = ({ onClose }) => {
  const { aduModels, poolModels, poolEquipment, aduEquipment } = useDesignStore()
  const [quote, setQuote] = useState<ProjectQuote | null>(null)
  const [loading, setLoading] = useState(false)
  
  // Combine all objects directly from store data
  const allObjects = useMemo(() => [...aduModels, ...poolModels], [aduModels, poolModels])

  const generateQuote = useCallback(async () => {
    setLoading(true)
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    try {
      // Combine all objects including equipment for comprehensive pricing
      const allProjectItems = [
        ...allObjects,
        ...poolEquipment,
        ...aduEquipment
      ]
      console.log('Generating quote for:', { 
        mainObjects: allObjects.length, 
        poolEquipment: poolEquipment.length, 
        aduEquipment: aduEquipment.length,
        total: allProjectItems.length 
      })
      
      const projectQuote = pricingEngine.generateProjectQuote(allProjectItems)
      setQuote(projectQuote)
    } catch (error) {
      console.error('Error generating quote:', error)
    } finally {
      setLoading(false)
    }
  }, [allObjects, poolEquipment, aduEquipment])

  useEffect(() => {
    if (allObjects.length > 0 || poolEquipment.length > 0 || aduEquipment.length > 0) {
      generateQuote()
    } else {
      setQuote(null)
    }
  }, [allObjects.length, poolEquipment.length, aduEquipment.length, generateQuote])

  const handleDownloadQuote = () => {
    if (!quote) return
    
    // Create a simple text version of the quote
    const quoteText = `
ServiceFlow Pro.ai - Project Quote
================================

${quote.objects.map(obj => `
${obj.itemName}
- Total: ${formatCurrency(obj.totalPrice)}
- Price per sq ft: ${formatCurrency(obj.pricePerSqFt)}
`).join('')}

Subtotal: ${formatCurrency(quote.subtotal)}
Tax: ${formatCurrency(quote.tax)}
Total: ${formatCurrency(quote.total)}

Timeline: ${quote.timeline}
Warranty: ${quote.warranty}

Notes:
${quote.notes.map(note => `- ${note}`).join('\n')}
    `.trim()

    const blob = new Blob([quoteText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'project-quote.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleShareQuote = () => {
    if (!quote) return
    
    const shareText = `Check out my project quote: ${formatCurrency(quote.total)} for ${quote.objects.length} item(s). Timeline: ${quote.timeline}`
    
    if (navigator.share) {
      navigator.share({
        title: 'Project Quote - ServiceFlow Pro.ai',
        text: shareText,
        url: window.location.href
      })
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareText)
      // You could show a toast notification here
    }
  }

  const getBreakdownColumns = () => [
    {
      title: 'Item',
      dataIndex: 'item',
      key: 'item',
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      render: (cost: number) => formatCurrency(cost),
      align: 'right' as const,
    },
  ]

  const renderBreakdownData = (breakdown: PricingBreakdown) => {
    const data: any[] = []
    
    // Safety check - ensure breakdown and breakdown.breakdown exist
    if (!breakdown || !breakdown.breakdown) {
      return data
    }
    
    // Materials
    if (breakdown.breakdown?.materials) {
      Object.entries(breakdown.breakdown.materials).forEach(([key, value]) => {
        if (value > 0) {
          data.push({
            key: `material-${key}`,
            item: `${key.charAt(0).toUpperCase() + key.slice(1)} Materials`,
            cost: value,
          })
        }
      })
    }
    
    // Features
    if (breakdown.breakdown?.features) {
      Object.entries(breakdown.breakdown.features).forEach(([key, value]) => {
        if (value > 0) {
          data.push({
            key: `feature-${key}`,
            item: `${key.charAt(0).toUpperCase() + key.slice(1)}`,
            cost: value,
          })
        }
      })
    }
    
    // Other costs
    if (breakdown.breakdown?.labor) {
      data.push({ key: 'labor', item: 'Labor', cost: breakdown.breakdown.labor })
    }
    if (breakdown.breakdown?.permits) {
      data.push({ key: 'permits', item: 'Permits', cost: breakdown.breakdown.permits })
    }
    if (breakdown.breakdown?.design) {
      data.push({ key: 'design', item: 'Design Services', cost: breakdown.breakdown.design })
    }
    if (breakdown.breakdown?.equipment) {
      data.push({ key: 'equipment', item: 'Equipment', cost: breakdown.breakdown.equipment })
    }
    if (breakdown.breakdown?.excavation) {
      data.push({ key: 'excavation', item: 'Excavation', cost: breakdown.breakdown.excavation })
    }
    
    return data
  }

  if (allObjects.length === 0) {
    return (
      <Card 
        title={
          <Space>
            <DollarOutlined />
            <Title level={4} style={{ margin: 0 }}>Pricing & Quotes</Title>
          </Space>
        }
        extra={
          <Button size="small" onClick={onClose} title="Close Panel">
            <CloseOutlined />
          </Button>
        }
      >
        <Alert
          message="No Objects to Quote"
          description="Add ADUs or Pools to your design to see pricing estimates."
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
        />
      </Card>
    )
  }

  return (
    <Card
      title={
        <Space>
          <DollarOutlined />
          <Title level={4} style={{ margin: 0 }}>Project Quote</Title>
        </Space>
      }
      extra={
        <Space>
          {quote && (
            <>
              <Button icon={<DownloadOutlined />} onClick={handleDownloadQuote}>
                Download
              </Button>
              <Button icon={<ShareAltOutlined />} onClick={handleShareQuote}>
                Share
              </Button>
            </>
          )}
          <Button size="small" onClick={onClose} title="Close Panel">
            <CloseOutlined />
          </Button>
        </Space>
      }
      loading={loading}
    >
      {quote ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Summary Cards */}
          <Row gutter={16}>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Total Investment"
                  value={quote.total}
                  formatter={(value) => formatCurrency(Number(value))}
                  valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Timeline"
                  value={quote.timeline}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ fontSize: '18px' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Warranty"
                  value={quote.warranty.split(',')[0]}
                  prefix={<SafetyOutlined />}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Project Items */}
          <div>
            <Title level={5}>Project Items</Title>
            <Collapse>
              {quote.objects.map((item, index) => (
                <Panel
                  header={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>
                        <Tag color={item.itemName.includes('ADU') ? 'blue' : 'green'}>
                          {item.itemName.includes('ADU') ? 'ADU' : 'POOL'}
                        </Tag>
                        {item.itemName}
                      </span>
                      <span style={{ fontWeight: 'bold' }}>
                        {formatCurrency(item.totalPrice)}
                      </span>
                    </div>
                  }
                  key={index}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text strong>Price per sq ft: </Text>
                      <Text>{formatCurrency(item.pricePerSqFt)}</Text>
                    </div>
                    
                    <Table
                      dataSource={renderBreakdownData(item)}
                      columns={getBreakdownColumns()}
                      pagination={false}
                      size="small"
                      summary={(pageData) => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            <Text strong>Subtotal</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <Text strong>{formatCurrency(item.totalPrice)}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  </Space>
                </Panel>
              ))}
            </Collapse>
          </div>

          {/* Cost Summary */}
          <Card size="small" title="Cost Summary">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Subtotal:</Text>
                <Text>{formatCurrency(quote.subtotal)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Tax:</Text>
                <Text>{formatCurrency(quote.tax)}</Text>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong style={{ fontSize: '16px' }}>Total:</Text>
                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  {formatCurrency(quote.total)}
                </Text>
              </div>
            </Space>
          </Card>

          {/* Project Notes */}
          <Card size="small" title="Important Notes">
            <Space direction="vertical">
              {quote.notes.map((note, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px', marginTop: '4px' }} />
                  <Text>{note}</Text>
                </div>
              ))}
            </Space>
          </Card>

          {/* Call to Action */}
          <Alert
            message="Ready to Get Started?"
            description="This quote is valid for 30 days. Contact us to schedule a consultation and begin your project."
            type="success"
            showIcon
            action={
              <Space direction="vertical">
                <Button type="primary" size="small">
                  Schedule Consultation
                </Button>
                <Button size="small">
                  Request Modifications
                </Button>
              </Space>
            }
          />
        </Space>
      ) : (
        <Alert
          message="Generating Quote..."
          description="Please wait while we calculate pricing for your project."
          type="info"
          showIcon
        />
      )}
    </Card>
  )
}

export default PricingPanel
